import { createContext, ReactNode, useEffect, useMemo, useReducer } from "react";
import appSlice, { initialState, setAuthHeaderSet, setLoggedinUser } from "./appSlice";
import { AppStateProps, ContextProps } from "./types";
import useAppActions from "./useAppActions";
import { RelativeComponentLoader } from "@/components/loader";
import { getUser } from "@/services/auth";
import { setAuthorizationHeader } from "@/helpers/api_helper";
import { getTenant } from "@/helpers/tenant";
import { posthogIdentify, sentrySetUser, rollbarSetUser } from "@/services/logging";
import useLocationTracker from "./useLocationTracker";

export const AppContext = createContext<ContextProps>({
  state: initialState,
  dispatch: () => null,
});

/**
 * True for a login made on the tenantless public-admin host (app.getaltimate.com).
 * `saveUser` (services/auth) stores `permission: null` only for those logins;
 * tenant users always carry a permission array (possibly empty). Kept local so
 * tests that partially mock `@/services/auth` are unaffected.
 */
const isPublicAdminUser = (user: AppStateProps["loggedinUser"]): boolean =>
  !!user && user.permission === null;

const AppProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [state, dispatch] = useReducer(appSlice.reducer, appSlice.getInitialState());

  useLocationTracker({ dispatch });

  const {
    fetchTenantInfo,
    loadingTenantInfo,
    setLoadingTenantInfo,
    fetchDatastores,
    loadingDatastores,
    setLoadingDatastores,
    fetchCompanies,
    loadingCompanies,
  } = useAppActions(dispatch);
  const tenant = getTenant();
  // Public-admin host: no tenant sub-domain AND the stored login is a public admin.
  // Tenant info and data stores live in per-tenant schemas, so the backend rejects
  // those two bootstrap requests for the public tenant (403) — do not send them.
  const isPublicAdminHost = !tenant && isPublicAdminUser(state.loggedinUser);

  useEffect(() => {
    const userFromLocalStorage = getUser();
    if (!userFromLocalStorage) {
      setLoadingTenantInfo(false);
      setLoadingDatastores(false);
      return;
    }
    // if app context does not have loggedin user, set it from local storage
    if (!state.loggedinUser) {
      dispatch(setLoggedinUser(userFromLocalStorage));
      posthogIdentify(userFromLocalStorage);
      sentrySetUser(userFromLocalStorage);
      rollbarSetUser(userFromLocalStorage);
      return;
    }

    if (state.loggedinUser) {
      setAuthorizationHeader(state.loggedinUser.auth_token);
      dispatch(setAuthHeaderSet(true));
      if (isPublicAdminHost) {
        // Release the two gates the skipped requests would have released.
        setLoadingTenantInfo(false);
        setLoadingDatastores(false);
      } else {
        fetchTenantInfo();
        fetchDatastores();
      }
      fetchCompanies();
    }
  }, [state.loggedinUser]);

  const values = useMemo(
    () => ({
      state,
      dispatch,
    }),
    [state, dispatch]
  );

  return (
    <AppContext.Provider value={values}>
      {/* Tenant hosts gate on tenant info + data stores. The public-admin host
          skips those two requests, so it gates on the company-info request it
          does make — the bootstrap loader still shows until data is ready. */}
      {loadingTenantInfo || loadingDatastores || (isPublicAdminHost && loadingCompanies) ? (
        <RelativeComponentLoader label={"Loading..."} />
      ) : (
        children
      )}
    </AppContext.Provider>
  );
};

export default AppProvider;
